import React, { Suspense, useEffect, useMemo, useState } from 'react'
import { Redirect, Route, Switch, useLocation } from 'react-router-dom'
import {
  IonApp,
  IonBadge,
  IonIcon,
  IonLabel,
  IonRouterOutlet,
  IonSpinner,
  IonTabBar,
  IonTabButton,
  IonTabs,
  IonToast,
  setupIonicReact,
} from '@ionic/react'
import IceModal from './components/IceModal'
import { IonReactRouter } from '@ionic/react-router'
import { logOutOutline, cashOutline, settingsOutline, cardOutline, statsChartOutline, alertCircleOutline } from 'ionicons/icons'
import { useRecoilState } from 'recoil'
import { useIsFetching, useQueryClient } from 'react-query'
import { useAuthQuery } from './helpers/http'
import { useBackendStatus } from './helpers/useBackendStatus'
import { RefreshCw } from 'lucide-react'
import LoginScreen from './screens/LoginScreen/LoginScreen'
import { user_state } from './states/user.state'
import { toast_state } from './states/toast.state'

/* Core CSS required for Ionic components to work properly */
import '@ionic/react/css/core.css'
import '@ionic/react/css/normalize.css'
import '@ionic/react/css/structure.css'
import '@ionic/react/css/typography.css'
import '@ionic/react/css/padding.css'
import '@ionic/react/css/float-elements.css'
import '@ionic/react/css/text-alignment.css'
import '@ionic/react/css/text-transformation.css'
import '@ionic/react/css/flex-utils.css'
import '@ionic/react/css/display.css'
import './theme/variables.css'
import './App.css'

// Après une MEP, un navigateur avec l'ancien main.js en cache demande des chunks
// qui n'existent plus sur le serveur (404) → page blanche. On recharge alors la page
// une seule fois (garde-fou sessionStorage anti-boucle) pour récupérer le build à jour.
let lazyRetry = (importFn: () => Promise<any>) =>
  React.lazy(() =>
    importFn().catch((err) => {
      let last = Number(sessionStorage.getItem('ice-chunk-reload') || 0)
      if (Date.now() - last > 30_000) {
        sessionStorage.setItem('ice-chunk-reload', String(Date.now()))
        window.location.reload()
        // Promesse jamais résolue : la page recharge, on évite le crash du Suspense
        return new Promise<never>(() => {})
      }
      throw err
    }),
  )

// Lazy loading — chaque page chargée à la demande (Leaflet+Recharts ne pèsent plus sur le bundle initial)
let TransactionsTab = lazyRetry(() => import('./screens/TransactionsTab'))
let MonitoringTab = lazyRetry(() => import('./screens/MonitoringTab'))
let HistoriqueMachineTab = lazyRetry(() => import('./screens/HistoriqueMachineTab'))
let GestionTab = lazyRetry(() => import('./screens/GestionTab'))
let StatistiquesTab = lazyRetry(() => import('./screens/StatistiquesTab'))
let EventTab = lazyRetry(() => import('./screens/EventTab'))

setupIonicReact()

// Résoudre le label de chargement à partir de la query key
let getLoadingLabel = (key: string): string => {
  if (key === 'transactions') return 'Chargement des transactions…'
  if (key === 'transactions-totals') return 'Calcul des totaux…'
  if (key === 'interventions') return 'Chargement du monitoring…'
  if (key === 'events') return 'Chargement des alertes…'
  if (key === 'statistics') return 'Calcul des statistiques…'
  if (key === 'cartes-commercant') return 'Chargement des cartes commerçant…'
  if (key === 'cartes-technicien') return 'Chargement des cartes technicien…'
  if (key === 'blacklist-cb') return 'Chargement de la blacklist CB…'
  if (key.startsWith('machine-')) return 'Chargement de la fiche machine…'
  return 'Chargement…'
}

// Mapping query key → route pour ne montrer le label que sur la bonne page
let queryKeyToRoute: Record<string, string> = {
  'transactions': '/transactions',
  'transactions-totals': '/transactions',
  'interventions': '/monitoring',
  'events': '/events',
  'statistics': '/statistiques',
  'cartes-commercant': '/gestion',
  'cartes-technicien': '/gestion',
  'blacklist-cb': '/gestion',
}

// Barre de chargement globale — label visible uniquement sur la page concernée
let GlobalLoadingBar: React.FC = () => {
  let isFetching = useIsFetching()
  let queryClient = useQueryClient()
  let location = useLocation()

  // Ne montrer le label que pour les queries de la page courante
  let label = useMemo(() => {
    if (isFetching === 0) return ''
    let queries = queryClient.getQueryCache().getAll()
    for (let q of queries) {
      if (q.state.isFetching) {
        let key = (Array.isArray(q.queryKey) ? q.queryKey[0] : q.queryKey) as string
        let route = queryKeyToRoute[key] || (key.startsWith('machine-') ? '/machine' : null)
        if (route && location.pathname.startsWith(route)) {
          return getLoadingLabel(key)
        }
      }
    }
    return ''
  }, [isFetching, location.pathname])

  return (
    <div className={`ice-nprogress ${isFetching ? 'active' : ''}`}>
      <div className="ice-nprogress-bar" />
      {label && <div className="ice-nprogress-label">{label}</div>}
    </div>
  )
}

// Badge compteur d'alertes du jour — disparaît quand on clique sur l'onglet
let EventsBadgeCount: React.FC = React.memo(() => {
  let { data: events } = useAuthQuery<any>('events', '/events', { refetchInterval: 60000 })
  let [dismissed, setDismissed] = useState(false)

  // Reset le dismissed quand les events changent (nouvelles alertes)
  let todayCount = useMemo(() => {
    if (!events) return 0
    let todayStart = new Date()
    todayStart.setHours(0, 0, 0, 0)
    let todayStartSec = todayStart.getTime() / 1000
    return events.filter((e: any) => e.horodatage >= todayStartSec).length
  }, [events])

  // Reset dismissed quand le count change
  useEffect(() => { setDismissed(false) }, [todayCount])

  if (todayCount === 0 || dismissed) return null
  return (
    <IonBadge
      color="danger"
      style={{ position: 'absolute', top: 2, right: 8, fontSize: '0.6rem', minWidth: 18, zIndex: 10, cursor: 'pointer' }}
      onClick={() => setDismissed(true)}
    >
      {todayCount}
    </IonBadge>
  )
})

// Fix Ionic scroll blanc — observer les mutations DOM pour forcer la visibilité
let IonicScrollFixer: React.FC = () => {
  useEffect(() => {
    // Observer le DOM pour détecter quand Ionic ajoute ion-page-invisible
    let observer = new MutationObserver(() => {
      // Trouver la page qui correspond à la route actuelle (pas invisible ET pas hidden)
      let activePage = document.querySelector('.ion-page:not(.ion-page-invisible):not(.ion-page-hidden)') as HTMLElement
      if (activePage) {
        activePage.style.zIndex = '101'
        activePage.style.opacity = '1'
        // Forcer scrollToTop sur son contenu
        let content = activePage.querySelector('ion-content') as any
        if (content?.scrollToTop) content.scrollToTop(0)
      }

      // Si TOUTES les pages sont invisibles (bug Ionic), forcer la dernière visible
      let allPages = document.querySelectorAll('.ion-page')
      let allHidden = Array.from(allPages).every(p =>
        p.classList.contains('ion-page-invisible') || p.classList.contains('ion-page-hidden')
      )
      if (allHidden && allPages.length > 0) {
        let last = allPages[allPages.length - 1] as HTMLElement
        last.classList.remove('ion-page-invisible', 'ion-page-hidden')
        last.style.zIndex = '101'
        last.style.opacity = '1'
        last.style.pointerEvents = 'auto'
        window.dispatchEvent(new Event('resize'))
      }
    })

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
    })

    return () => observer.disconnect()
  }, [])
  return null
}

// Toast global — accessible depuis n'importe quel hook/composant via toast_state
// Durée : 5s pour les erreurs, 3s pour le reste
let GlobalToast: React.FC = () => {
  let [toast, setToast] = useRecoilState(toast_state)
  let isError = toast?.color === 'danger' || toast?.color === 'warning'
  return (
    <IonToast
      isOpen={!!toast}
      onDidDismiss={() => setToast(null)}
      message={toast?.message || ''}
      color={toast?.color || 'primary'}
      duration={isError ? 5000 : 3000}
      position="top"
    />
  )
}

// Bannière sticky quand le backend est injoignable
let BackendBanner: React.FC = () => {
  let { status, recheck } = useBackendStatus()
  let [rechecking, setRechecking] = useState(false)

  let handleRecheck = async () => {
    setRechecking(true)
    await recheck()
    setTimeout(() => setRechecking(false), 1000)
  }

  // Pas de bannière tant qu'on sait pas (checking) ou que tout va bien (connected)
  if (status === 'connected' || status === 'checking') return null

  return (
    <div className={`ice-backend-banner ${status === 'degraded' ? 'warning' : 'danger'}`}>
      <span>{status === 'degraded' ? 'Base de données inaccessible — certaines données peuvent être manquantes.' : 'Serveur injoignable — les données affichées peuvent être obsolètes.'}</span>
      <button onClick={handleRecheck} disabled={rechecking}>
        <RefreshCw size={12} className={rechecking ? 'ice-spin' : ''} />
        Réessayer
      </button>
    </div>
  )
}

// Non connecté : on mémorise la page demandée (ex. lien « Fiche machine » d'un mail d'alerte)
// pour y revenir après la connexion, au lieu d'atterrir sur Transactions
let RedirectToLogin: React.FC = () => {
  let location = useLocation()
  let cible = location.pathname + location.search
  if (cible !== '/' && !cible.startsWith('/login')) {
    try { sessionStorage.setItem('page_apres_connexion', cible) } catch { /* stockage indisponible : on ignore */ }
  }
  return <Redirect to="/login" />
}

const App: React.FC = () => {
  const [user, set_user] = useRecoilState(user_state)
  const [confirmLogout, setConfirmLogout] = useState(false)

  return (
    <IonApp>
      <GlobalToast />
      <IceModal
        isOpen={confirmLogout}
        onClose={() => setConfirmLogout(false)}
        title="Se déconnecter"
        message="Voulez-vous vraiment vous déconnecter ?"
        size="sm"
        buttons={[{ text: 'Déconnexion', color: 'danger', handler: () => {
          set_user(null)
          // B8 — Forcer un refresh complet pour vider tous les caches Recoil/React Query
          window.location.href = '/'
        } }]}
      />
      <IonReactRouter>
        <GlobalLoadingBar />
        <IonicScrollFixer />
        {user && <BackendBanner />}
        <Switch>
          <Route path="/login">
            <LoginScreen />
          </Route>
          {user ? (
            <IonTabs>
              <IonRouterOutlet>
                <Route exact path="/transactions">
                  <Suspense fallback={<IonSpinner className="ice-page-loader" />}>
                    <TransactionsTab />
                  </Suspense>
                </Route>

                <Route exact path="/monitoring">
                  <Suspense fallback={<IonSpinner className="ice-page-loader" />}>
                    <MonitoringTab />
                  </Suspense>
                </Route>

                <Route exact path="/events">
                  <Suspense fallback={<IonSpinner className="ice-page-loader" />}>
                    <EventTab />
                  </Suspense>
                </Route>

                <Route exact path="/gestion">
                  <Suspense fallback={<IonSpinner className="ice-page-loader" />}>
                    <GestionTab />
                  </Suspense>
                </Route>

                <Route exact path="/statistiques">
                  <Suspense fallback={<IonSpinner className="ice-page-loader" />}>
                    <StatistiquesTab />
                  </Suspense>
                </Route>

                <Route exact path="/machine/:machineId">
                  <Suspense fallback={<IonSpinner className="ice-page-loader" />}>
                    <HistoriqueMachineTab />
                  </Suspense>
                </Route>

                <Route exact path="/">
                  <Redirect to="/transactions" />
                </Route>
              </IonRouterOutlet>

              <IonTabBar slot="bottom">
                <IonTabButton tab="transactions" href="/transactions">
                  <IonIcon aria-hidden="true" icon={cashOutline} />
                  <IonLabel>Transactions</IonLabel>
                </IonTabButton>
                <IonTabButton tab="monitoring" href="/monitoring">
                  <IonIcon aria-hidden="true" icon={settingsOutline} />
                  <IonLabel>Monitoring</IonLabel>
                </IonTabButton>
                <IonTabButton tab="events" href="/events" style={{ position: 'relative' }}>
                  <IonIcon aria-hidden="true" icon={alertCircleOutline} />
                  <IonLabel>Evènements</IonLabel>
                  <EventsBadgeCount />
                </IonTabButton>
                <IonTabButton tab="gestion" href="/gestion">
                  <IonIcon aria-hidden="true" icon={cardOutline} />
                  <IonLabel>Gestion</IonLabel>
                </IonTabButton>
                <IonTabButton tab="statistiques" href="/statistiques">
                  <IonIcon aria-hidden="true" icon={statsChartOutline} />
                  <IonLabel>Statistiques</IonLabel>
                </IonTabButton>
                <IonTabButton
                  onClick={(e) => {
                    e.preventDefault()
                    setConfirmLogout(true)
                  }}
                  tab="logout"
                >
                  <IonIcon color="danger" aria-hidden="true" icon={logOutOutline} />
                  <IonLabel color="danger">Se déconnecter</IonLabel>
                </IonTabButton>
              </IonTabBar>

            </IonTabs>
          ) : (
            <RedirectToLogin />
          )}
        </Switch>
      </IonReactRouter>
    </IonApp>
  )
}

export default App
